import type { SiteConfig } from '@/lib/config/siteConfig.shared';

/**
 * Tenant theme → CSS custom properties.
 *
 * ONE source of truth for the mapping. It is used twice:
 *   1. Server-side, rendered as an inline <style> by SiteConfigServerProvider,
 *      so the FIRST paint already carries the tenant palette and font.
 *   2. Client-side, by ThemeStyleInjector, so /admin/branding previews and
 *      config changes apply without a reload.
 *
 * Everything here comes from `company_settings` (category `theme`):
 *   primary_color/_dark/_light   button colour            → --primary-red …
 *   link_color/link_hover        links, prices, icons     → --brand-link, tints --brand-link-50…500
 *   accent_color/accent_hover    badge backgrounds        → --sb-gold (+ contrast-safe text variant)
 *   gray_palette (JSON)          surfaces, borders, text  → --theme-gray-*, --sb-*, --heading-charcoal …
 *   footer_color                 footer background        → --brand-footer
 *   sale_color/sale_hover        sale / urgency accent    → --sale
 *   font_primary/font_secondary  body / heading families  → --font-primary, --font-heading
 *
 * The token NAMES are historical (crimson/gold era); the values are whatever
 * the tenant sets. See docs/BRANDING.md "Colour roles".
 */

// WCAG relative-luminance contrast ratio between a hex foreground and white background.
export function contrastOnWhite(hex: string): number {
  const m = hex.match(/^#?([a-f0-9]{6})$/i);
  if (!m) return 21; // unknown → don't downshift
  const rgb = m[1].match(/.{2}/g)!.map((h) => parseInt(h, 16) / 255);
  const lin = rgb.map((c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)));
  const L = 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
  return 1.05 / (L + 0.05);
}

// Safe text variant of a brand accent for use on white backgrounds. A tenant
// accent that fails WCAG AA on white (<4.5:1) is swapped for a fallback in
// text contexts; the raw accent is kept for badges/backgrounds.
export function safeAccentForText(hex: string, fallback = '#1B3A6B'): string {
  return contrastOnWhite(hex) >= 4.5 ? hex : fallback;
}

/** Mix a hex colour with white: t=0 → colour, t=1 → white. */
export function tintWithWhite(hex: string, t: number): string {
  const m = hex.match(/^#?([a-f0-9]{6})$/i);
  if (!m) return hex;
  const ch = m[1].match(/.{2}/g)!.map((h) => parseInt(h, 16));
  const out = ch.map((c) => Math.round(c + (255 - c) * t).toString(16).padStart(2, '0'));
  return `#${out.join('')}`;
}

const STAR_COLOR = '#FACC15'; // rating stars — fixed amber, never the tenant accent

/**
 * Tenant font families that app/layout.tsx self-hosts with next/font, mapped
 * to that font's CSS variable. These never go to the Google Fonts stylesheet.
 */
const SELF_HOSTED_FONTS: Record<string, string> = {
  'open sans': 'var(--font-open-sans)',
};

/** CSS font-family list for a Google/system family name, with a sans fallback stack. */
function fontStack(family: string | undefined | null): string | undefined {
  const f = (family || '').trim();
  if (!f) return undefined;
  const selfHosted = SELF_HOSTED_FONTS[f.toLowerCase()];
  return `${selfHosted ? `${selfHosted}, ` : ''}"${f.replace(/"/g, '')}", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif`;
}

export function buildThemeVars(config: SiteConfig): Record<string, string> {
  const t = config.theme;
  const vars: Record<string, string> = {};
  const set = (k: string, v: string | undefined | null) => {
    if (v) vars[k] = v;
  };

  // Primary (button) colour
  set('--primary-red', t.primaryColor);
  set('--primary-dark', t.primaryDark);
  set('--primary-light', t.primaryLight);
  set('--sb-red', t.primaryColor);
  set('--sb-red-hover', t.primaryDark);

  // Link / foreground brand colour + tints (the blog's `primary-50…500` scale)
  const link = t.linkColor || t.primaryColor;
  const linkHover = t.linkHover || (t.linkColor ? t.linkColor : t.primaryDark);
  set('--brand-link', link);
  set('--brand-link-hover', linkHover);
  if (link) {
    set('--brand-link-50', tintWithWhite(link, 0.94));
    set('--brand-link-100', tintWithWhite(link, 0.88));
    set('--brand-link-200', tintWithWhite(link, 0.75));
    set('--brand-link-300', tintWithWhite(link, 0.6));
    set('--brand-link-400', tintWithWhite(link, 0.35));
    set('--brand-link-500', tintWithWhite(link, 0.15));
  }

  // Accent (badges/backgrounds) + a contrast-safe text variant
  const accent = t.accentColor;
  const accentTextSafe = accent ? safeAccentForText(accent, link || '#1B3A6B') : undefined;
  set('--accent-yellow', accent);
  set('--accent-yellow-hover', t.accentHover);
  set('--accent-gold', accent);
  set('--sb-gold', accent);
  set('--sb-gold-text', accentTextSafe);
  set('--sb-gold-hover', t.accentHover);
  set('--heading-gold', accentTextSafe);
  set('--star', STAR_COLOR);

  set('--secondary-color', t.secondaryColor);

  // Sale / urgency accent
  set('--sale', t.saleColor);
  set('--sale-hover', t.saleHover || t.saleColor);

  // Gray palette — drives bg-gray-*, text-gray-*, border-gray-* and the sb-* surfaces
  const gp = t.grayPalette;
  if (gp) {
    set('--theme-gray-50', gp.gray50);
    set('--theme-gray-100', gp.gray100);
    set('--theme-gray-200', gp.gray200);
    set('--theme-gray-300', gp.gray300);
    set('--theme-gray-400', gp.gray400);
    set('--theme-gray-500', gp.gray500);
    set('--theme-gray-600', gp.gray600);
    set('--theme-gray-700', gp.gray700);
    set('--theme-gray-800', gp.gray800);
    set('--theme-gray-900', gp.gray900);
    set('--theme-gray-950', gp.gray950);

    set('--sb-bg', gp.gray50);
    set('--sb-bg-alt', gp.gray100);
    set('--sb-gold-tint', gp.gray100); // hover surface follows the ramp
    set('--sb-border', gp.gray300);
    set('--sb-text-muted', gp.gray600);
    set('--sb-text', gp.gray900);
    set('--heading-charcoal', gp.gray900);

    set('--neutral-ivory', gp.gray50);
    set('--neutral-cream', gp.gray100);
    set('--neutral-linen', gp.gray200);
    set('--neutral-gray', gp.gray300);
    set('--neutral-dark', gp.gray900);
    set('--bg-main', gp.gray50);
    set('--bg-ivory', gp.gray50);
    set('--bg-cream', gp.gray100);
    set('--bg-linen', gp.gray200);
    set('--text-default', gp.gray900);
    set('--text-primary', gp.gray900);
    set('--text-secondary', gp.gray700);
    set('--text-muted', gp.gray600);
    set('--brand-navtext', gp.gray800);
  }

  // Header / footer surfaces
  set('--brand-footer', t.footerColor);
  set('--bg-footer', t.footerColor || gp?.gray900 || t.primaryColor);
  set('--bg-header', '#FFFFFF');
  set('--brand-header', '#FFFFFF');
  set('--bg-header-top', gp?.gray900 || t.primaryDark);
  set('--bg-charcoal', gp?.gray900 || '#2E2E2E');

  // Fonts — DB-driven families. globals.css and tailwind put these first in
  // every stack; the Google Fonts <link> for them is rendered by the provider.
  set('--font-primary', fontStack(config.fonts?.primary));
  set('--font-heading', fontStack(config.fonts?.secondary || config.fonts?.primary));

  // Status + hover
  set('--status-info', t.primaryColor);
  set('--status-warning', t.accentColor);
  set('--hover-primary', t.primaryDark);
  set('--hover-yellow', t.accentHover);

  return vars;
}

/** `:root{--a:b;…}` for a server-rendered <style>. Values are validated. */
export function themeVarsCss(config: SiteConfig): string {
  const safe = /^[#a-zA-Z0-9(),.%"\s-]+$/;
  const body = Object.entries(buildThemeVars(config))
    .filter(([, v]) => safe.test(v))
    .map(([k, v]) => `${k}:${v}`)
    .join(';');
  return `:root{${body}}`;
}

/** Google Fonts stylesheet URL for the tenant's families (deduped), or null. */
export function googleFontsHref(config: SiteConfig): string | null {
  const fams = Array.from(
    new Set([config.fonts?.primary, config.fonts?.secondary].map((f) => (f || '').trim()).filter(Boolean))
  ).filter((f) => /^[A-Za-z0-9 ]+$/.test(f) && !SELF_HOSTED_FONTS[f.toLowerCase()]);
  if (!fams.length) return null;
  const q = fams.map((f) => `family=${f.replace(/ /g, '+')}:wght@400;500;600;700`).join('&');
  return `https://fonts.googleapis.com/css2?${q}&display=swap`;
}
